import { useEffect, useState } from 'react';

// Module-level registry shared by every hook instance so the same font
// family is never injected twice, and so we can track in-flight loads
// across re-renders/remounts (which happen constantly in a virtualized
// list as cards scroll in and out of view).
const injectedFamilies = new Map(); // family -> 'loading' | 'loaded' | 'error'
const listeners = new Map(); // family -> Set<callback>

function notify(family) {
  listeners.get(family)?.forEach((cb) => cb(injectedFamilies.get(family)));
}

function formatToMimeHint(format) {
  const map = {
    woff2: 'woff2',
    woff: 'woff',
    ttf: 'truetype',
  };
  return map[format] || format;
}

/**
 * Injects a single @font-face rule for the given font entity into a
 * shared <style> tag in <head>, but only the first time it's actually
 * needed — i.e. right before a FontCard renders text with it. This is
 * what keeps a 1000-font library from downloading megabytes of binary
 * assets on initial page load: the browser only fetches a font's bytes
 * once its @font-face rule exists AND something on the page uses that
 * font-family.
 *
 * Returns a status string: 'idle' | 'loading' | 'loaded' | 'error'.
 */
export function useFontInjector(font) {
  const [status, setStatus] = useState(
    font ? injectedFamilies.get(font.family) || 'idle' : 'idle'
  );

  useEffect(() => {
    if (!font) return undefined;
    const { family, format } = font;
    const cleanedPath = font.url.replace(/http:\/\/localhost:\d+/, '');
    const url = cleanedPath.startsWith('http') 
      ? font.url 
      : `${window.location.origin}${cleanedPath.startsWith('/') ? '' : '/'}${cleanedPath}`;

    const handleUpdate = (next) => setStatus(next);

    if (!listeners.has(family)) listeners.set(family, new Set());
    listeners.get(family).add(handleUpdate);

    const existing = injectedFamilies.get(family);
    if (existing) {
      setStatus(existing);
    } else {
      injectedFamilies.set(family, 'loading');
      notify(family);
      setStatus('loading');

      try {
        const styleTagId = 'font-previewer-dynamic-faces';
        let styleTag = document.getElementById(styleTagId);
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = styleTagId;
          document.head.appendChild(styleTag);
        }

        const rule = `
@font-face {
  font-family: "${family}";
  src: url("${url}") format("${formatToMimeHint(format)}");
  font-display: swap;
}
`;
        styleTag.appendChild(document.createTextNode(rule));

        // Use the FontFace API (when available) to know precisely when
        // the binary has actually finished downloading & parsing, rather
        // than guessing with a timeout.
        if ('fonts' in document) {
          const fontFace = new FontFace(family, `url(${url})`);
          fontFace
            .load()
            .then((loaded) => {
              document.fonts.add(loaded);
              injectedFamilies.set(family, 'loaded');
              notify(family);
            })
            .catch((err) => {
              console.warn(`Font failed to load: ${family}`, err);
              injectedFamilies.set(family, 'error');
              notify(family);
            });
        } else {
          // Fallback for older browsers without the FontFace API: assume
          // loaded once the CSS rule is attached (font-display: swap will
          // still handle the flash-of-fallback-text gracefully).
          injectedFamilies.set(family, 'loaded');
          notify(family);
        }
      } catch (err) {
        console.error(`Failed to inject @font-face for ${family}:`, err);
        injectedFamilies.set(family, 'error');
        notify(family);
      }
    }

    return () => {
      listeners.get(family)?.delete(handleUpdate);
    };
  }, [font]);

  return status;
}
